/**
 * Rides response schemas — shapes returned by the /api/rides/* endpoints.
 * Mirrors the `RideRecord` interface in `@kabbi/shared/types/ride`.
 *
 * Wire shapes (11 endpoints):
 *   - GET    /api/rides/today                → { rides: RideRecord[] }
 *   - GET    /api/rides                      → { rides: RideRecord[] }
 *   - GET    /api/rides/:id                  → { ride: RideRecord }
 *   - POST   /api/rides                      → RideRecord (bare, 201)
 *   - PATCH  /api/rides/:id                  → RideRecord (bare)
 *   - DELETE /api/rides/:id                  → { success, mode: 'hard' | 'soft' }
 *   - POST   /api/rides/:id/decline          → RideRecord (bare)
 *   - POST   /api/rides/:id/status           → RideRecord (bare)
 *   - POST   /api/rides/:id/cancel           → RideRecord (bare)
 *   - DELETE /api/rides/:id/permanent        → { success }
 *   - DELETE /api/rides/:id/purge            → { success }
 *
 * `RideRecord.status` and `.source` stay as `z.string()` — Prisma columns
 * are enums, but the schema matches the existing TS interface (string) so
 * admin's downstream `RIDE_STATUSES.X === ride.status` comparisons keep
 * working without narrowing.
 */
import { z } from 'zod';
import { RIDE_CANCEL_REASONS, type RideCancelReason } from '../../constants/index.js';
import { DocumentOwnerSummarySchema } from './documents.js';

const rideCancelReasonEnum = z.enum(
  Object.values(RIDE_CANCEL_REASONS) as [RideCancelReason, ...RideCancelReason[]],
);

/** GeoJSON LineString — the cached polyline from the routing provider. */
const GeoJsonLineStringSchema = z.object({
  type: z.literal('LineString'),
  coordinates: z.array(z.tuple([z.number(), z.number()])),
});

/**
 * Wire shape of one stop on a ride. See `Waypoint` in
 * `@kabbi/shared/types/ride` for prose docs on every field. Stored on
 * the `Ride.waypoints` Json column; the API validates the array length
 * (>= 2) at the route boundary, not via Zod (refinement instead).
 */
export const WaypointSchema = z.object({
  address: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  title: z.string().nullable(),
  comment: z.string().nullable(),
  addressInfo: z.string().nullable(),
  waitTimeMin: z.number().nullable(),
  datetime: z.string().nullable(),
  // Per-stop passenger identity for GROUP orders (multi-passenger recurring
  // templates): each pickup waypoint names the person collected there. Nullish
  // — absent on COMMON/MEDICAL stops and on all pre-GROUP rides.
  passengerFirstName: z.string().nullish(),
  passengerLastName: z.string().nullish(),
  passengerPhone: z.string().nullish(),
});

/**
 * Linked customer summary, attached to a ride ONLY on `GET /api/rides/:id`
 * (so the order dialog can re-load the customer's phone + saved addresses on
 * edit). Omitted from list responses — those don't join the relation.
 */
const RidePassengerUserSchema = z.object({
  id: z.string(),
  phone: z.string().nullable(),
  // Coordinates travel with the address: the order dialog's saved-address
  // sidebar writes them straight onto a waypoint ("Set pickup"/"Set dest"),
  // which needs real coords to stay routable.
  addresses: z.array(z.object({
    id: z.string(), label: z.string(), address: z.string(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
    isHome: z.boolean().optional(),
  })),
});

/**
 * Snapshot of a vehicle feature selected on a ride. Denormalized on purpose:
 * the feature's id + both localized names + category are frozen onto the ride
 * at save time, so an order keeps rendering the feature even after the catalog
 * row is (soft-)deleted — with no live join. Stored in `Ride.extraCategories`.
 */
export const rideExtraFeatureSchema = z.object({
  id: z.string(),
  nameDe: z.string(),
  nameEn: z.string(),
  category: z.string(),
});

/**
 * Snapshot of the payment option selected on a ride. Frozen at save time from
 * the dynamic PAYMENT OptionCatalog so the order keeps its payment label even
 * after the option is soft-deleted. Stored in `Ride.paymentOption`.
 */
export const ridePaymentOptionSchema = z.object({
  id: z.string(),
  // Stable machine key (e.g. CASH/CARD) snapshotted from OptionCatalog.key.
  // Nullish: null for label-only options, absent on legacy snapshots.
  key: z.string().nullish(),
  nameDe: z.string(),
  nameEn: z.string(),
});

/**
 * Shared field-group for the order/passenger snapshot block, split into two
 * flavors because request bodies and response records apply different
 * nullable/optional modifiers to the SAME logical fields:
 *
 *  - `orderSnapshotRequestShape`  — permissive create/edit body flavor: every
 *    field `.nullable().optional()` (personCount additionally `.positive()`,
 *    array fields just `.optional()`). Spread into `rideBodySchema`
 *    (forms/scheduler) and `recurringTripBodySchema` (forms/recurringTrip).
 *  - `orderSnapshotResponseShape` — serialized-record flavor: every field
 *    `.nullable()` (never optional; personCount without `.positive()`). Spread
 *    into `RideRecordSchema` (below) and `RecurringTripRecordSchema`.
 *
 * Each consumer spreads the matching shape and layers its own remaining fields
 * on top, so every resulting schema's inferred type + validation is byte-for-
 * byte what it was when the fields were spelled out inline. Fields whose
 * variance differs across consumers (e.g. `passengerUserId` — a bare string on
 * recurring records but a nested `passengerUser` object on ride records) are
 * intentionally left inline in each schema, not shared here.
 */
export const orderSnapshotRequestShape = {
  passengerFirstName:    z.string().nullable().optional(),
  passengerLastName:     z.string().nullable().optional(),
  passengerPhone:        z.string().nullable().optional(),
  passengerUserId:       z.string().nullable().optional(),
  vehicleTypeRequiredId: z.string().nullable().optional(),
  waypoints:             z.array(WaypointSchema).optional(),
  paymentOption:         ridePaymentOptionSchema.nullable().optional(),
  extraCategories:       z.array(rideExtraFeatureSchema).optional(),
  personCount:           z.number().int().positive().nullable().optional(),
  fare:                  z.number().nullable().optional(),
  notes:                 z.string().nullable().optional(),
  type:                  z.enum(['COMMON', 'MEDICAL', 'GROUP']).optional(),
};

export const orderSnapshotResponseShape = {
  passengerFirstName:       z.string().nullable(),
  passengerLastName:        z.string().nullable(),
  passengerPhone:           z.string().nullable(),
  vehicleTypeRequiredId:    z.string().nullable(),
  vehicleTypeRequiredKey:   z.string().nullable(),
  vehicleTypeRequiredLabel: z.string().nullable(),
  waypoints:                z.array(WaypointSchema),
  paymentOption:            ridePaymentOptionSchema.nullable(),
  extraCategories:          z.array(rideExtraFeatureSchema),
  personCount:              z.number().int().nullable(),
  fare:                     z.number().nullable(),
  notes:                    z.string().nullable(),
  type:                     z.enum(['COMMON', 'MEDICAL', 'GROUP']),
};

/**
 * Mirror of the `RideRecord` interface from `@kabbi/shared/types/ride`.
 * Produced by `toRecord()` in `apps/api/src/routes/rides.ts`.
 *
 * The order/passenger snapshot fields come from `orderSnapshotResponseShape`
 * (passenger names + phone, vehicle-type id/key/label, waypoints, paymentOption,
 * extraCategories, personCount, fare, notes, type); the ride-only fields below
 * (ids, scheduling, routing, driver/disponent, cancel, `passengerUser`) stay
 * inline.
 */
export const RideRecordSchema = z.object({
  ...orderSnapshotResponseShape,
  id: z.string(),
  trackingNumber: z.number().int(),
  /** Derived `ORD-YYYY-{trackingNumber}`. */
  orderNumber: z.string(),
  status: z.string(),
  source: z.string(),
  scheduledStart: z.string().nullable(),
  scheduledEnd: z.string().nullable(),
  /** Planned pickup time of the round-trip return leg (ISO). Null = one-way. */
  returnDatetime: z.string().nullable(),
  /**
   * Linked customer id — flat, and present on EVERY response (list included),
   * unlike `passengerUser` below. Any flow that re-creates a ride from a list
   * row (auto-assign's return leg) needs it to carry the customer link over.
   */
  passengerUserId: z.string().nullable(),
  /** Payer organization id, or null when the order names no payer. */
  organizationId: z.string().nullable().optional(),
  /** Linked customer (id + phone + addresses). Present only on GET /:id. */
  passengerUser: RidePassengerUserSchema.nullish(),
  distance: z.number().nullable(),
  duration: z.number().nullable(),
  routeGeometry: GeoJsonLineStringSchema.nullable(),
  fleetVehicleId: z.string().nullable(),
  vehiclePlate: z.string().nullable(),
  vehicleModel: z.string().nullable(),
  driverId: z.string().nullable(),
  driverName: z.string().nullable(),
  disponentId: z.string().nullable(),
  disponentName: z.string().nullable(),
  declinable: z.boolean(),
  cancelReason: rideCancelReasonEnum.nullable(),
  cancelReasonText: z.string().nullable(),
  createdAt: z.string(),
});

/**
 * One row of the Orders list: the ride plus its document roll-up.
 *
 * `documents` is on the LIST row rather than `RideRecordSchema` so the
 * single-ride GET, create and update responses are unaffected — they have no
 * use for it and would pay a query for nothing. Same arrangement as the Fleet
 * list. Optional, so an older client tolerates its absence.
 */
export const RideListRowSchema = RideRecordSchema.extend({
  documents: DocumentOwnerSummarySchema.optional(),
});

export const RideListResponseSchema = z.object({
  rides: z.array(RideListRowSchema),
});

export const RideResponseSchema = z.object({
  ride: RideRecordSchema,
});

/** Response from DELETE /api/rides/:id — distinguishes hard vs soft delete. */
export const RideDeleteResponseSchema = z.object({
  success: z.boolean(),
  mode: z.enum(['hard', 'soft']),
});

/** Response from DELETE /api/rides/:id/permanent and /purge. */
export const RideSuccessResponseSchema = z.object({
  success: z.boolean(),
});

export type RideListResponse = z.infer<typeof RideListResponseSchema>;
export type RideListRow = z.infer<typeof RideListRowSchema>;
export type RideResponse = z.infer<typeof RideResponseSchema>;
export type RideDeleteResponse = z.infer<typeof RideDeleteResponseSchema>;
export type RideSuccessResponse = z.infer<typeof RideSuccessResponseSchema>;
